import React, { useEffect, useState } from "react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { api, apiErr } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { toast } from "sonner";
import { Gift, Loader2, ShieldCheck, Check } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1544639044-4f142ceb6a2b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function GiftCardPage() {
  const { t } = useI18n();
  const [amounts, setAmounts] = useState([50, 100, 150, 200]);
  const [amount, setAmount] = useState(100);
  const [customMode, setCustomMode] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [bounds, setBounds] = useState({ min: 1, max: 2000 });
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "", recipient_name: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/giftcards/options").then((r) => {
      setAmounts(r.data.amounts);
      setAmount(r.data.amounts[1] || r.data.amounts[0]);
      setBounds({ min: r.data.custom_min ?? 1, max: r.data.custom_max ?? 2000 });
    }).catch(() => {});
  }, []);

  const submit = async () => {
    const amt = customMode ? Number(customAmount) : amount;
    if (!form.buyer_name.trim() || !form.buyer_email.trim()) {
      toast.error(t("giftPage.requiredError"));
      return;
    }
    if (!amt || amt < bounds.min || amt > bounds.max) {
      toast.error(t("giftPage.customError").replace("{min}", bounds.min).replace("{max}", bounds.max));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/giftcards/checkout", {
        amount: amt, ...form, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiErr(e));
      setLoading(false);
    }
  };

  const effAmount = customMode ? Number(customAmount || 0) : amount;

  return (
    <div data-testid="giftcard-page">
      <PageHero title={t("giftPage.title")} subtitle={t("giftPage.subtitle")} image={HERO} crumb={t("giftPage.crumb")} />

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* left: pitch + how it works */}
          <Reveal>
            <p className="text-brand font-accent text-2xl italic mb-3">{t("giftPage.crumb")}</p>
            <p className="text-ink-600 font-body leading-relaxed mb-8">{t("giftPage.intro")}</p>
            <h2 className="font-heading text-2xl text-ink-900 mb-4">{t("giftPage.howtoTitle")}</h2>
            <ol className="space-y-3">
              {t("giftPage.howto").map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-ink-700 font-body text-sm">
                  <span className="w-6 h-6 rounded-full bg-brand text-white flex items-center justify-center text-xs shrink-0 mt-0.5">{i + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </Reveal>

          {/* right: form card */}
          <Reveal delay={120}>
            <div className="rounded-3xl bg-white border border-black/5 shadow-[0_16px_48px_rgba(44,48,48,0.08)] p-6 sm:p-8">
              <div className="flex items-center gap-2 text-brand mb-5">
                <Gift size={22} /> <span className="font-heading text-xl">{t("giftPage.chooseAmount")}</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {amounts.map((a) => (
                  <button key={a} onClick={() => { setCustomMode(false); setAmount(a); }} data-testid={`gift-amount-${a}`}
                    className={`rounded-xl border-2 py-4 font-heading text-lg transition-colors ${!customMode && amount === a ? "border-brand bg-brand text-white" : "border-ink-400/20 text-ink-900 hover:border-brand/50"}`}>
                    {a} €
                  </button>
                ))}
                <button onClick={() => setCustomMode(true)} data-testid="gift-amount-custom"
                  className={`col-span-2 sm:col-span-4 rounded-xl border-2 py-3.5 font-heading text-base transition-colors ${customMode ? "border-brand bg-brand text-white" : "border-ink-400/20 text-ink-900 hover:border-brand/50"}`}>
                  {t("giftPage.customAmount")}
                </button>
              </div>

              {customMode && (
                <div className="mb-6">
                  <div className="relative">
                    <input type="number" min={bounds.min} max={bounds.max} step="1" value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)} autoFocus
                      placeholder={t("giftPage.customPlaceholder")}
                      className="w-full rounded-xl border-2 border-brand/40 px-4 py-3.5 pr-10 font-heading text-lg outline-none focus:border-brand"
                      data-testid="gift-custom-input" />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-500 font-heading text-lg">€</span>
                  </div>
                  <p className="mt-2 text-xs text-ink-400">{t("giftPage.customError").replace("{min}", bounds.min).replace("{max}", bounds.max)}</p>
                </div>
              )}

              <div className="space-y-3">
                <input value={form.buyer_name} onChange={(e) => setForm((f) => ({ ...f, buyer_name: e.target.value }))}
                  placeholder={t("giftPage.buyerName")} className="w-full rounded-lg border border-ink-400/25 px-4 py-3 text-sm outline-none focus:border-brand" data-testid="gift-buyer-name" />
                <input type="email" value={form.buyer_email} onChange={(e) => setForm((f) => ({ ...f, buyer_email: e.target.value }))}
                  placeholder={t("giftPage.buyerEmail")} className="w-full rounded-lg border border-ink-400/25 px-4 py-3 text-sm outline-none focus:border-brand" data-testid="gift-buyer-email" />
                <input value={form.recipient_name} onChange={(e) => setForm((f) => ({ ...f, recipient_name: e.target.value }))}
                  placeholder={t("giftPage.recipientName")} className="w-full rounded-lg border border-ink-400/25 px-4 py-3 text-sm outline-none focus:border-brand" data-testid="gift-recipient" />
                <textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} rows={3}
                  placeholder={t("giftPage.message")} className="w-full rounded-lg border border-ink-400/25 px-4 py-3 text-sm outline-none focus:border-brand resize-none" data-testid="gift-message" />
              </div>

              <button onClick={submit} disabled={loading} className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-6 py-3.5 font-medium hover:bg-brand-600 transition-colors disabled:opacity-60" data-testid="gift-submit">
                {loading ? <><Loader2 size={18} className="animate-spin" /> {t("giftPage.processing")}</> : <><Gift size={18} /> {t("giftPage.cta")} · {effAmount} €</>}
              </button>

              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-ink-400"><ShieldCheck size={14} /> {t("giftPage.securePay")}</p>
              <p className="mt-2 flex items-start gap-1.5 text-xs text-ink-500"><Check size={14} className="text-brand mt-0.5 shrink-0" /> {t("giftPage.validity")}</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
